import { Link, useRouterState } from '@tanstack/react-router';
import { Icon, IconName, Typography } from '@/primer/components';

interface NavTab {
  path: string;
  label: string;
  icon: IconName;
  activeIcon: IconName;
}

const NAV_TABS: NavTab[] = [
  {
    path: '/dashboard',
    label: 'Home',
    icon: 'home',
    activeIcon: 'home',
  },
  {
    path: '/support',
    label: 'Support',
    icon: 'circle-question-mark',
    activeIcon: 'circle-question-mark',
  },
  {
    path: '/settings',
    label: 'Settings',
    icon: 'cog',
    activeIcon: 'cog',
  },
];

/**
 * AppHeader — desktop-only top navigation bar.
 *
 * Rendered as a floating pill/card that sits above the page content.
 * Hidden on mobile (< 800px); visible on tablet and desktop (md:+).
 *
 * Active route detection is path-prefix based so nested routes (e.g.
 * /campaign/123) correctly highlight the parent tab.
 */
export function AppHeader() {
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;

  return (
    <header
      className="hidden md:block fixed top-0 left-0 right-0 z-50 px-6 pt-4 h-[var(--app-header-height)]"
      data-testid="app-header"
    >
      <div className="bg-surface-translucent rounded-full shadow-(--shadow-top-light-ground-floor) flex items-center justify-between px-5 py-3 mx-auto max-w-screen-lg">
        {/* Logo */}
        <Link to="/dashboard" className="flex items-center no-underline shrink-0">
          <img
            src="/assets/images/logo-black.svg"
            alt="Influenster for Creators"
            className="h-7 logo-adaptive"
          />
        </Link>

        {/* Nav links */}
        <nav aria-label="Main navigation">
          <ul className="flex items-center gap-1 list-none m-0 p-0">
            {NAV_TABS.map((tab) => {
              const isActive = currentPath === tab.path || currentPath.startsWith(`${tab.path}/`);

              return (
                <li key={tab.path}>
                  <Link
                    to={tab.path}
                    aria-current={isActive ? 'page' : undefined}
                    className={`
                      flex items-center gap-2 px-4 py-2 rounded-full transition-colors no-underline
                    `}
                  >
                    <Icon
                      name={isActive ? tab.activeIcon : tab.icon}
                      color={isActive ? 'interactive' : 'default'}
                    />
                    <Typography
                      variant="button"
                      size="small"
                      color={isActive ? 'interactive' : 'body'}
                    >
                      {tab.label}
                    </Typography>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </header>
  );
}
